import * as THREE from "three";

import type { Placement } from "../types";
import { createOpenPlansId } from "../utils";
import {
  type OpenElementDefinition,
  type OpenElementDimensions,
  OpenElement,
} from "../elements/generic-element";

export interface OPBaseExternalAsset {
  id: string;
  kind: "external-op";
  uri: string;
}

export interface OPBaseEmbeddedAsset {
  id: string;
  kind: "embedded-op";
  definition: OpenElementDefinition;
}

export type OPBaseAsset = OPBaseExternalAsset | OPBaseEmbeddedAsset;

export interface OPBaseNodeOverrides {
  labelName?: string;
  metadata?: Record<string, string>;
  dimensions?: OpenElementDimensions;
}

export interface OPBaseNode {
  id: string;
  assetRef: string;
  parentId?: string;
  placement: Placement;
  visible?: boolean;
  locked?: boolean;
  overrides?: OPBaseNodeOverrides;
}

export interface AttachmentSpec {
  hostAnchorId?: string;
  childAnchorId?: string;
  offset?: Placement;
  alignment?: "match" | "flipX" | "flipY" | "flipZ";
  face?: string;
  slot?: string;
}

export interface OPBaseRelation {
  id: string;
  kind: "attachedTo" | "hostedBy" | "insertedInto" | "dependsOn" | "groupMember";
  from: string;
  to: string;
  attachment?: AttachmentSpec;
  metadata?: Record<string, string>;
}

export interface OPBaseDocument {
  version: 1;
  id: string;
  name: string;
  units: "meters";
  assets: Record<string, OPBaseAsset>;
  nodes: Record<string, OPBaseNode>;
  roots: string[];
  relations?: Record<string, OPBaseRelation>;
  metadata?: Record<string, string>;
}

export interface OPBaseConfig extends OPBaseDocument {
  ogid?: string;
}

export interface OPBaseResolvedAsset {
  id: string;
  kind: OPBaseAsset["kind"];
  definition: OpenElementDefinition;
  sourceUri?: string;
}

export interface OPBaseResolvedNode {
  id: string;
  assetRef: string;
  parentId?: string;
  visible: boolean;
  locked: boolean;
  localPlacement: Placement;
  worldPlacement: Placement;
  childIds: string[];
  relationIds: string[];
  container: THREE.Group;
  element: OpenElement;
  overrides?: OPBaseNodeOverrides;
}

export interface OPBaseResolvedDocument {
  id: string;
  name: string;
  units: "meters";
  metadata?: Record<string, string>;
  assets: Record<string, OPBaseResolvedAsset>;
  nodes: Record<string, OPBaseResolvedNode>;
  roots: string[];
  relations: Record<string, OPBaseRelation>;
  elements: OpenElement[];
}

export interface OPBaseOptions extends Partial<Omit<OPBaseConfig, "version" | "assets" | "nodes" | "roots">> {
  version?: 1;
  assets?: Record<string, OPBaseAsset>;
  nodes?: Record<string, OPBaseNode>;
  roots?: string[];
  relations?: Record<string, OPBaseRelation>;
  source?: string | OPBaseDocument;
  assetBaseUrl?: string;
  assetSources?: Record<string, string | OpenElementDefinition>;
}

export type OPBaseInput = string | OPBaseDocument | OPBaseOptions;
export type OPBaseLoadOverrides = Partial<Omit<OPBaseOptions, "source">>;

interface ResolvedOPBaseRuntimeConfig {
  document: OPBaseConfig;
  assetBaseUrl?: string;
  assetSources: Record<string, string | OpenElementDefinition>;
}

const DEFAULT_PLACEMENT: Required<Placement> = {
  anchor: [0, 0, 0],
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function sanitizeMetadata(metadata?: Record<string, string>): Record<string, string> | undefined {
  if (!metadata) {
    return undefined;
  }

  const entries = Object.entries(metadata)
    .filter(([key]) => key.length > 0)
    .map(([key, value]) => [key, String(value)] as const);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizePlacement(placement?: Placement): Required<Placement> {
  const source = placement ?? DEFAULT_PLACEMENT;
  const anchor = Array.isArray(source.anchor) ? source.anchor : DEFAULT_PLACEMENT.anchor;
  const position = Array.isArray(source.position) ? source.position : DEFAULT_PLACEMENT.position;
  const rotation = Array.isArray(source.rotation) ? source.rotation : DEFAULT_PLACEMENT.rotation;
  const scale = Array.isArray(source.scale) ? source.scale : DEFAULT_PLACEMENT.scale;

  return {
    anchor: [
      toFiniteNumber(anchor[0], DEFAULT_PLACEMENT.anchor[0]),
      toFiniteNumber(anchor[1], DEFAULT_PLACEMENT.anchor[1]),
      toFiniteNumber(anchor[2], DEFAULT_PLACEMENT.anchor[2]),
    ],
    position: [
      toFiniteNumber(position[0], DEFAULT_PLACEMENT.position[0]),
      toFiniteNumber(position[1], DEFAULT_PLACEMENT.position[1]),
      toFiniteNumber(position[2], DEFAULT_PLACEMENT.position[2]),
    ],
    rotation: [
      toFiniteNumber(rotation[0], DEFAULT_PLACEMENT.rotation[0]),
      toFiniteNumber(rotation[1], DEFAULT_PLACEMENT.rotation[1]),
      toFiniteNumber(rotation[2], DEFAULT_PLACEMENT.rotation[2]),
    ],
    scale: [
      toFiniteNumber(scale[0], DEFAULT_PLACEMENT.scale[0]),
      toFiniteNumber(scale[1], DEFAULT_PLACEMENT.scale[1]),
      toFiniteNumber(scale[2], DEFAULT_PLACEMENT.scale[2]),
    ],
  };
}

function normalizeDimensions(
  dimensions?: OpenElementDimensions,
  fallback?: OpenElementDimensions,
): OpenElementDimensions | undefined {
  if (!dimensions && !fallback) {
    return undefined;
  }

  const source = dimensions ?? fallback;
  if (!source) {
    return undefined;
  }

  return {
    width: Math.max(0.01, toFiniteNumber(source.width, fallback?.width ?? 1)),
    depth: Math.max(0.01, toFiniteNumber(source.depth, fallback?.depth ?? 1)),
    height: Math.max(0.01, toFiniteNumber(source.height, fallback?.height ?? 1)),
  };
}

function degToRad(deg: number): number {
  return deg * (Math.PI / 180);
}

function radToDeg(rad: number): number {
  return rad * (180 / Math.PI);
}

function createPlacementMatrix(placement?: Placement): THREE.Matrix4 {
  const normalized = normalizePlacement(placement);
  const anchor = new THREE.Vector3(...normalized.anchor);
  const position = new THREE.Vector3(...normalized.position);
  const rotation = new THREE.Euler(
    degToRad(normalized.rotation[0]),
    degToRad(normalized.rotation[1]),
    degToRad(normalized.rotation[2]),
  );
  const quaternion = new THREE.Quaternion().setFromEuler(rotation);
  const scale = new THREE.Vector3(...normalized.scale);

  // M = T(position) * T(anchor) * R * S * T(-anchor) — rotation/scale pivot at anchor.
  const negAnchor = anchor.clone().multiplyScalar(-1);
  const tNegAnchor = new THREE.Matrix4().makeTranslation(negAnchor.x, negAnchor.y, negAnchor.z);
  const rs = new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0), quaternion, scale);
  const tAnchor = new THREE.Matrix4().makeTranslation(anchor.x, anchor.y, anchor.z);
  const tPosition = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z);

  return tPosition.multiply(tAnchor).multiply(rs).multiply(tNegAnchor);
}

function matrixToPlacement(matrix?: THREE.Matrix4): Placement {
  if (!matrix) {
    return cloneValue(DEFAULT_PLACEMENT);
  }

  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix.decompose(position, quaternion, scale);
  const rotation = new THREE.Euler().setFromQuaternion(quaternion);

  return {
    position: [position.x, position.y, position.z],
    rotation: [radToDeg(rotation.x), radToDeg(rotation.y), radToDeg(rotation.z)],
    scale: [scale.x, scale.y, scale.z],
  };
}

function combinePlacements(parent?: Placement, child?: Placement): Placement {
  const combined = createPlacementMatrix(parent).multiply(createPlacementMatrix(child));
  return matrixToPlacement(combined);
}

function invertPlacement(placement?: Placement): Placement {
  const inverse = createPlacementMatrix(placement);
  inverse.invert();
  return matrixToPlacement(inverse);
}

function resolveRuntimeUrl(url: string, baseUrl?: string) {
  if (baseUrl) {
    try {
      return new URL(url, baseUrl).toString();
    } catch {
      if (typeof window !== "undefined" && typeof window.location?.href === "string") {
        const absoluteBase = new URL(baseUrl, window.location.href).toString();
        return new URL(url, absoluteBase).toString();
      }

      throw new Error(`Failed to resolve URL "${url}" against base "${baseUrl}".`);
    }
  }

  if (typeof window !== "undefined" && typeof window.location?.href === "string") {
    return new URL(url, window.location.href).toString();
  }

  return url;
}

function applyPlacementToObject(object: THREE.Object3D, placement?: Placement) {
  const matrix = createPlacementMatrix(placement);
  // Use matrixAutoUpdate=false so the composed matrix (which encodes the
  // anchor pivot) is preserved; otherwise THREE recomposes from
  // position/rotation/scale and loses the anchor compensation.
  object.matrix.copy(matrix);
  object.matrixAutoUpdate = false;
  object.updateMatrixWorld(true);

  const decomposedPosition = new THREE.Vector3();
  const decomposedQuaternion = new THREE.Quaternion();
  const decomposedScale = new THREE.Vector3();
  matrix.decompose(decomposedPosition, decomposedQuaternion, decomposedScale);
  object.position.copy(decomposedPosition);
  object.quaternion.copy(decomposedQuaternion);
  object.scale.copy(decomposedScale);
}

function normalizeRuntimeAssetBaseUrl(value: unknown) {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function normalizeRuntimeAssetSources(value: unknown): Record<string, string | OpenElementDefinition> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, source]) => [
      key,
      typeof source === "string" ? source : OpenElement.normalizeDefinition(source),
    ]),
  );
}

function normalizeNodeOverrides(value: unknown): OPBaseNodeOverrides | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const dimensions = normalizeDimensions(value.dimensions as OpenElementDimensions | undefined);
  const metadata = sanitizeMetadata(value.metadata as Record<string, string> | undefined);
  const labelName = typeof value.labelName === "string" && value.labelName.length > 0
    ? value.labelName
    : undefined;

  if (!labelName && !metadata && !dimensions) {
    return undefined;
  }

  return {
    labelName,
    metadata,
    dimensions,
  };
}

function normalizeAttachmentSpec(value: unknown, context: string): AttachmentSpec | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!isRecord(value)) {
    throw new Error(`${context} must be an object.`);
  }

  const alignment = value.alignment;
  if (
    alignment !== undefined
    && alignment !== "match"
    && alignment !== "flipX"
    && alignment !== "flipY"
    && alignment !== "flipZ"
  ) {
    throw new Error(`${context}.alignment must be "match", "flipX", "flipY", or "flipZ".`);
  }

  return {
    hostAnchorId: typeof value.hostAnchorId === "string" && value.hostAnchorId.length > 0
      ? value.hostAnchorId
      : undefined,
    childAnchorId: typeof value.childAnchorId === "string" && value.childAnchorId.length > 0
      ? value.childAnchorId
      : undefined,
    offset: value.offset ? normalizePlacement(value.offset as Placement) : undefined,
    alignment,
    face: typeof value.face === "string" && value.face.length > 0 ? value.face : undefined,
    slot: typeof value.slot === "string" && value.slot.length > 0 ? value.slot : undefined,
  };
}

function normalizeAsset(value: unknown, key: string): OPBaseAsset {
  if (!isRecord(value)) {
    throw new Error(`assets.${key} must be an object.`);
  }

  const id = typeof value.id === "string" && value.id.length > 0 ? value.id : key;

  if (value.kind === "external-op") {
    if (typeof value.uri !== "string" || value.uri.length === 0) {
      throw new Error(`assets.${key}.uri must be a non-empty string.`);
    }

    return {
      id,
      kind: "external-op",
      uri: value.uri,
    };
  }

  if (value.kind === "embedded-op") {
    return {
      id,
      kind: "embedded-op",
      definition: OpenElement.normalizeDefinition(value.definition),
    };
  }

  throw new Error(`assets.${key}.kind must be "external-op" or "embedded-op".`);
}

function normalizeNode(value: unknown, key: string): OPBaseNode {
  if (!isRecord(value)) {
    throw new Error(`nodes.${key} must be an object.`);
  }
  if (typeof value.assetRef !== "string" || value.assetRef.length === 0) {
    throw new Error(`nodes.${key}.assetRef must be a non-empty string.`);
  }

  return {
    id: typeof value.id === "string" && value.id.length > 0 ? value.id : key,
    assetRef: value.assetRef,
    parentId: typeof value.parentId === "string" && value.parentId.length > 0 ? value.parentId : undefined,
    placement: normalizePlacement(value.placement as Placement | undefined),
    visible: value.visible === undefined ? true : Boolean(value.visible),
    locked: value.locked === undefined ? false : Boolean(value.locked),
    overrides: normalizeNodeOverrides(value.overrides),
  };
}

function normalizeRelation(value: unknown, key: string): OPBaseRelation {
  if (!isRecord(value)) {
    throw new Error(`relations.${key} must be an object.`);
  }
  if (
    value.kind !== "attachedTo"
    && value.kind !== "hostedBy"
    && value.kind !== "insertedInto"
    && value.kind !== "dependsOn"
    && value.kind !== "groupMember"
  ) {
    throw new Error(`relations.${key}.kind is unsupported.`);
  }
  if (typeof value.from !== "string" || value.from.length === 0) {
    throw new Error(`relations.${key}.from must be a non-empty string.`);
  }
  if (typeof value.to !== "string" || value.to.length === 0) {
    throw new Error(`relations.${key}.to must be a non-empty string.`);
  }

  return {
    id: typeof value.id === "string" && value.id.length > 0 ? value.id : key,
    kind: value.kind,
    from: value.from,
    to: value.to,
    attachment: normalizeAttachmentSpec(value.attachment, `relations.${key}.attachment`),
    metadata: sanitizeMetadata(value.metadata as Record<string, string> | undefined),
  };
}

function normalizeAssets(value: unknown): Record<string, OPBaseAsset> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, asset]) => [key, normalizeAsset(asset, key)]),
  );
}

function normalizeNodes(value: unknown): Record<string, OPBaseNode> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, node]) => [key, normalizeNode(node, key)]),
  );
}

function normalizeRelations(value: unknown): Record<string, OPBaseRelation> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const relations = Object.fromEntries(
    Object.entries(value).map(([key, relation]) => [key, normalizeRelation(relation, key)]),
  );

  return Object.keys(relations).length > 0 ? relations : undefined;
}

function normalizeRoots(value: unknown, nodes: Record<string, OPBaseNode>): string[] {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((root, index) => {
      if (typeof root !== "string" || root.length === 0) {
        throw new Error(`roots[${index}] must be a non-empty string.`);
      }
      return root;
    });
  }

  return Object.values(nodes)
    .filter((node) => !node.parentId)
    .map((node) => node.id);
}

function parseOPBaseBuffer(source: string): OPBaseDocument {
  let parsed: unknown;

  try {
    parsed = JSON.parse(source);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Invalid .opbase buffer: ${message}`);
  }

  if (!isRecord(parsed)) {
    throw new Error("Invalid .opbase buffer: root value must be an object.");
  }

  return normalizeOPBaseDocument(parsed);
}

function normalizeOPBaseDocument(input: unknown): OPBaseDocument {
  if (!isRecord(input)) {
    throw new Error("Project definition must be an object.");
  }

  const nodes = normalizeNodes(input.nodes);
  const units = input.units === undefined ? "meters" : input.units;
  if (units !== "meters") {
    throw new Error(`Unsupported .opbase units "${String(units)}". Only "meters" is currently supported.`);
  }

  return {
    version: 1,
    id: typeof input.id === "string" && input.id.length > 0 ? input.id : createOpenPlansId("opbase"),
    name: typeof input.name === "string" && input.name.length > 0 ? input.name : "OpenPlans Project",
    units,
    assets: normalizeAssets(input.assets),
    nodes,
    roots: normalizeRoots(input.roots, nodes),
    relations: normalizeRelations(input.relations),
    metadata: sanitizeMetadata(input.metadata as Record<string, string> | undefined),
  };
}

function isDocumentCandidate(value: OPBaseInput): value is OPBaseDocument {
  return isRecord(value) && value.version === 1 && isRecord(value.assets) && isRecord(value.nodes);
}

function resolveOPBaseRuntimeConfig(
  input?: OPBaseInput,
  current?: ResolvedOPBaseRuntimeConfig,
): ResolvedOPBaseRuntimeConfig {
  const baseDocument = current?.document ?? normalizeOPBaseDocument({});

  if (input === undefined) {
    return {
      document: cloneValue(baseDocument),
      assetBaseUrl: current?.assetBaseUrl,
      assetSources: { ...(current?.assetSources ?? {}) },
    };
  }

  if (typeof input === "string") {
    return {
      document: {
        ...parseOPBaseBuffer(input),
        ogid: current?.document.ogid,
      },
      assetBaseUrl: current?.assetBaseUrl,
      assetSources: { ...(current?.assetSources ?? {}) },
    };
  }

  if (isRecord(input) && Object.prototype.hasOwnProperty.call(input, "source") && input.source !== undefined) {
    const sourceDocument = typeof input.source === "string"
      ? parseOPBaseBuffer(input.source)
      : normalizeOPBaseDocument(input.source);

    const mergedDocument = normalizeOPBaseDocument({
      ...sourceDocument,
      id: input.id ?? sourceDocument.id,
      name: input.name ?? sourceDocument.name,
      units: input.units ?? sourceDocument.units,
      assets: input.assets ?? sourceDocument.assets,
      nodes: input.nodes ?? sourceDocument.nodes,
      roots: input.roots ?? sourceDocument.roots,
      relations: input.relations ?? sourceDocument.relations,
      metadata: input.metadata ?? sourceDocument.metadata,
    });

    return {
      document: {
        ogid: typeof input.ogid === "string" && input.ogid.length > 0
          ? input.ogid
          : current?.document.ogid,
        ...mergedDocument,
      },
      assetBaseUrl: normalizeRuntimeAssetBaseUrl(input.assetBaseUrl) ?? current?.assetBaseUrl,
      assetSources: {
        ...(current?.assetSources ?? {}),
        ...normalizeRuntimeAssetSources(input.assetSources),
      },
    };
  }

  if (isDocumentCandidate(input)) {
    return {
      document: {
        ogid: current?.document.ogid,
        ...normalizeOPBaseDocument(input),
      },
      assetBaseUrl: current?.assetBaseUrl,
      assetSources: { ...(current?.assetSources ?? {}) },
    };
  }

  const mergedDocument = normalizeOPBaseDocument({
    ...baseDocument,
    ...input,
    assets: input.assets ?? baseDocument.assets,
    nodes: input.nodes ?? baseDocument.nodes,
    roots: input.roots ?? baseDocument.roots,
    relations: input.relations ?? baseDocument.relations,
    metadata: input.metadata ?? baseDocument.metadata,
  });

  return {
    document: {
      ogid: typeof input.ogid === "string" && input.ogid.length > 0
        ? input.ogid
        : current?.document.ogid,
      ...mergedDocument,
    },
    assetBaseUrl: normalizeRuntimeAssetBaseUrl(input.assetBaseUrl) ?? current?.assetBaseUrl,
    assetSources: {
      ...(current?.assetSources ?? {}),
      ...normalizeRuntimeAssetSources(input.assetSources),
    },
  };
}

function toSerializableDocument(config: OPBaseConfig): OPBaseDocument {
  return {
    version: 1,
    id: config.id,
    name: config.name,
    units: config.units,
    assets: cloneValue(config.assets),
    nodes: cloneValue(config.nodes),
    roots: cloneValue(config.roots),
    relations: config.relations ? cloneValue(config.relations) : undefined,
    metadata: config.metadata ? cloneValue(config.metadata) : undefined,
  };
}

function isAttachmentRelationKind(kind: OPBaseRelation["kind"]) {
  return kind === "attachedTo" || kind === "hostedBy" || kind === "insertedInto";
}

function buildChildMap(nodes: Record<string, OPBaseNode>) {
  const childIds = new Map<string, string[]>();

  Object.values(nodes).forEach((node) => {
    if (!node.parentId) {
      return;
    }

    const existing = childIds.get(node.parentId) ?? [];
    existing.push(node.id);
    childIds.set(node.parentId, existing);
  });

  return childIds;
}

function buildRelationIdsByNode(relations: Record<string, OPBaseRelation>) {
  const relationIds = new Map<string, string[]>();

  Object.values(relations).forEach((relation) => {
    const from = relationIds.get(relation.from) ?? [];
    from.push(relation.id);
    relationIds.set(relation.from, from);

    const to = relationIds.get(relation.to) ?? [];
    to.push(relation.id);
    relationIds.set(relation.to, to);
  });

  return relationIds;
}

function resolveAssetAnchorPlacement(
  definition: OpenElementDefinition,
  anchorId: string | undefined,
  context: string,
): Placement {
  const assetPlacement = normalizePlacement(definition.placement);

  if (!anchorId) {
    return assetPlacement;
  }

  const anchor = definition.anchors?.[anchorId];
  if (!anchor) {
    throw new Error(`${context} references unknown anchor "${anchorId}" on asset "${definition.id}".`);
  }

  return combinePlacements(assetPlacement, anchor.placement);
}

function applyAttachmentAlignment(placement: Placement, alignment?: AttachmentSpec["alignment"]) {
  if (!alignment || alignment === "match") {
    return placement;
  }

  const next = cloneValue(placement);
  if (alignment === "flipX") {
    next.scale[0] *= -1;
  } else if (alignment === "flipY") {
    next.scale[1] *= -1;
  } else if (alignment === "flipZ") {
    next.scale[2] *= -1;
  }

  return next;
}

function resolveNodeLocalPlacement(
  node: OPBaseNode,
  document: OPBaseConfig,
  assets: Record<string, OPBaseResolvedAsset>,
): Placement {
  const relations = Object.values(document.relations ?? {}).filter((relation) =>
    relation.from === node.id && isAttachmentRelationKind(relation.kind),
  );

  if (relations.length > 1) {
    throw new Error(`Node "${node.id}" has multiple attachment-style relations. Only one is allowed.`);
  }

  if (relations.length === 0) {
    return normalizePlacement(node.placement);
  }

  const relation = relations[0];
  if (node.parentId !== relation.to) {
    throw new Error(`Attachment relation "${relation.id}" requires node "${node.id}" to have parentId "${relation.to}".`);
  }

  const hostNode = document.nodes[relation.to];
  if (!hostNode) {
    throw new Error(`Attachment relation "${relation.id}" references unknown host node "${relation.to}".`);
  }

  const hostAsset = assets[hostNode.assetRef];
  const childAsset = assets[node.assetRef];
  if (!hostAsset || !childAsset) {
    throw new Error(`Attachment relation "${relation.id}" could not resolve host or child assets.`);
  }

  const hostAnchorPlacement = resolveAssetAnchorPlacement(
    hostAsset.definition,
    relation.attachment?.hostAnchorId,
    `relations.${relation.id}.attachment.hostAnchorId`,
  );
  const childAnchorPlacement = resolveAssetAnchorPlacement(
    childAsset.definition,
    relation.attachment?.childAnchorId,
    `relations.${relation.id}.attachment.childAnchorId`,
  );
  const alignmentPlacement = applyAttachmentAlignment(
    normalizePlacement(node.placement),
    relation.attachment?.alignment,
  );
  const attachmentOffset = normalizePlacement(relation.attachment?.offset);

  return combinePlacements(
    hostAnchorPlacement,
    combinePlacements(
      attachmentOffset,
      combinePlacements(alignmentPlacement, invertPlacement(childAnchorPlacement)),
    ),
  );
}

function validateProjectDocument(
  document: OPBaseConfig,
  assets: Record<string, OPBaseResolvedAsset>,
) {
  Object.entries(document.assets).forEach(([key, asset]) => {
    if (asset.id !== key) {
      throw new Error(`assets.${key}.id must match its record key.`);
    }
  });

  Object.entries(document.nodes).forEach(([key, node]) => {
    if (node.id !== key) {
      throw new Error(`nodes.${key}.id must match its record key.`);
    }
    if (!assets[node.assetRef]) {
      throw new Error(`nodes.${key}.assetRef references unknown asset "${node.assetRef}".`);
    }
    if (node.parentId && !document.nodes[node.parentId]) {
      throw new Error(`nodes.${key}.parentId references unknown node "${node.parentId}".`);
    }
  });

  Object.entries(document.relations ?? {}).forEach(([key, relation]) => {
    if (relation.id !== key) {
      throw new Error(`relations.${key}.id must match its record key.`);
    }
    if (!document.nodes[relation.from]) {
      throw new Error(`relations.${key}.from references unknown node "${relation.from}".`);
    }
    if (!document.nodes[relation.to]) {
      throw new Error(`relations.${key}.to references unknown node "${relation.to}".`);
    }
    if (isAttachmentRelationKind(relation.kind) && document.nodes[relation.from]?.parentId !== relation.to) {
      throw new Error(`Attachment relation "${relation.id}" must align with parentId on node "${relation.from}".`);
    }
  });

  document.roots.forEach((rootId) => {
    const rootNode = document.nodes[rootId];
    if (!rootNode) {
      throw new Error(`roots references unknown node "${rootId}".`);
    }
    if (rootNode.parentId) {
      throw new Error(`Root node "${rootId}" cannot also define parentId "${rootNode.parentId}".`);
    }
  });

  Object.values(document.nodes)
    .filter((node) => !node.parentId && !document.roots.includes(node.id))
    .forEach((node) => {
      throw new Error(`Top-level node "${node.id}" must appear in roots.`);
    });

  const visited = new Set<string>();
  const stack = new Set<string>();

  const visit = (nodeId: string) => {
    if (stack.has(nodeId)) {
      throw new Error(`Parent cycle detected in .opbase hierarchy at node "${nodeId}".`);
    }
    if (visited.has(nodeId)) {
      return;
    }

    stack.add(nodeId);
    const parentId = document.nodes[nodeId]?.parentId;
    if (parentId) {
      visit(parentId);
    }
    stack.delete(nodeId);
    visited.add(nodeId);
  };

  Object.keys(document.nodes).forEach((nodeId) => visit(nodeId));
}

function resolveAssetSourceKeyCandidates(asset: OPBaseExternalAsset, baseUrl?: string) {
  const candidates = [asset.id, asset.uri];

  if (baseUrl || typeof window !== "undefined") {
    try {
      candidates.push(resolveRuntimeUrl(asset.uri, baseUrl));
    } catch {
      // Ignore invalid URL combinations and keep earlier candidates.
    }
  }

  return candidates;
}

function filterIfcElements(elements: OpenElement[]) {
  return elements.filter((element) =>
    element.ogType === "WALL" || element.ogType === "DOOR" || element.ogType === "WINDOW",
  );
}

export async function loadOPBaseAssetSources(
  document: OPBaseDocument,
  assetBaseUrl?: string,
): Promise<Record<string, string>> {
  const sources: Record<string, string> = {};

  await Promise.all(
    Object.values(document.assets)
      .filter((asset): asset is OPBaseExternalAsset => asset.kind === "external-op")
      .map(async (asset) => {
        const resolvedUrl = resolveRuntimeUrl(asset.uri, assetBaseUrl);
        const response = await fetch(resolvedUrl);
        if (!response.ok) {
          throw new Error(`Failed to load external asset "${asset.uri}": ${response.status} ${response.statusText}`);
        }

        const source = await response.text();
        sources[asset.id] = source;
        sources[asset.uri] = source;
        sources[resolvedUrl] = source;
      }),
  );

  return sources;
}

export class OPBaseProject extends THREE.Group {
  ogid: string;

  propertySet: OPBaseConfig = {
    version: 1,
    id: createOpenPlansId("opbase"),
    name: "OpenPlans Project",
    units: "meters",
    assets: {},
    nodes: {},
    roots: [],
    relations: undefined,
    metadata: undefined,
  };

  private assetBaseUrl?: string;
  private assetSources: Record<string, string | OpenElementDefinition> = {};
  private opBaseBuffer = "";
  private resolvedDocument: OPBaseResolvedDocument | null = null;

  constructor(config?: OPBaseInput) {
    super();

    const runtimeConfig = resolveOPBaseRuntimeConfig(config);
    this.propertySet = runtimeConfig.document;
    this.assetBaseUrl = runtimeConfig.assetBaseUrl;
    this.assetSources = runtimeConfig.assetSources;
    this.ogid = this.propertySet.ogid ?? this.propertySet.id;
    this.userData.ogid = this.ogid;
    this.name = this.propertySet.name;

    this.refreshBuffer();
    this.rebuildProject();
  }

  static parseBuffer(buffer: string): OPBaseDocument {
    return parseOPBaseBuffer(buffer);
  }

  static normalizeDocument(document: unknown): OPBaseDocument {
    return normalizeOPBaseDocument(document);
  }

  getOPBaseBuffer() {
    return this.opBaseBuffer;
  }

  getOPBaseConfig() {
    return cloneValue(this.propertySet);
  }

  setOPBaseConfig(config: OPBaseInput) {
    const runtimeConfig = resolveOPBaseRuntimeConfig(config, {
      document: this.propertySet,
      assetBaseUrl: this.assetBaseUrl,
      assetSources: this.assetSources,
    });

    this.propertySet = runtimeConfig.document;
    this.assetBaseUrl = runtimeConfig.assetBaseUrl;
    this.assetSources = runtimeConfig.assetSources;
    this.ogid = this.propertySet.ogid ?? this.propertySet.id;
    this.name = this.propertySet.name;
    this.userData.ogid = this.ogid;

    this.refreshBuffer();
    this.rebuildProject();
  }

  setNodePlacement(nodeId: string, placement: Placement) {
    const node = this.propertySet.nodes[nodeId];
    if (!node) {
      throw new Error(`Unknown project node "${nodeId}".`);
    }

    node.placement = normalizePlacement(placement);
    this.refreshBuffer();
    this.rebuildProject();
  }

  getAsset(id: string) {
    return this.resolvedDocument?.assets[id];
  }

  getNode(id: string) {
    return this.resolvedDocument?.nodes[id];
  }

  getRelation(id: string) {
    return this.resolvedDocument?.relations[id];
  }

  getExportElements() {
    return this.resolvedDocument?.elements ?? [];
  }

  resolveProject() {
    if (!this.resolvedDocument) {
      this.rebuildProject();
    }

    return this.resolvedDocument as OPBaseResolvedDocument;
  }

  generateExportDocument() {
    return this.resolveProject();
  }

  dispose() {
    this.clearResolvedScene();
  }

  private refreshBuffer() {
    this.opBaseBuffer = JSON.stringify(toSerializableDocument(this.propertySet), null, 2);
  }

  private rebuildProject() {
    this.clearResolvedScene();

    const assets = this.resolveAssets();
    validateProjectDocument(this.propertySet, assets);

    const relations = this.propertySet.relations ?? {};
    const relationIdsByNode = buildRelationIdsByNode(relations);
    const childIdsByNode = buildChildMap(this.propertySet.nodes);
    const localPlacements = new Map<string, Placement>();

    Object.values(this.propertySet.nodes).forEach((node) => {
      localPlacements.set(node.id, resolveNodeLocalPlacement(node, this.propertySet, assets));
    });

    const nodes: Record<string, OPBaseResolvedNode> = {};
    const elements: OpenElement[] = [];

    const buildNode = (
      nodeId: string,
      parentContainer?: THREE.Group,
      parentWorldPlacement?: Placement,
    ) => {
      const node = this.propertySet.nodes[nodeId];
      const asset = assets[node.assetRef];
      const localPlacement = cloneValue(localPlacements.get(node.id) ?? normalizePlacement(node.placement));
      const worldPlacement = parentWorldPlacement
        ? combinePlacements(parentWorldPlacement, localPlacement)
        : cloneValue(localPlacement);

      const container = new THREE.Group();
      container.name = node.id;
      container.userData.ogid = node.id;
      applyPlacementToObject(container, localPlacement);
      container.visible = node.visible !== false;

      const element = new OpenElement({
        source: asset.definition,
        placement: DEFAULT_PLACEMENT,
        ogid: node.id,
        labelName: node.overrides?.labelName,
        metadata: node.overrides?.metadata,
        dimensions: node.overrides?.dimensions,
      });

      container.add(element);
      (parentContainer ?? this).add(container);

      const childIds = [...(childIdsByNode.get(node.id) ?? [])];
      const relationIds = [...(relationIdsByNode.get(node.id) ?? [])];

      nodes[node.id] = {
        id: node.id,
        assetRef: node.assetRef,
        parentId: node.parentId,
        visible: node.visible !== false,
        locked: node.locked === true,
        localPlacement,
        worldPlacement,
        childIds,
        relationIds,
        container,
        element,
        overrides: node.overrides ? cloneValue(node.overrides) : undefined,
      };

      elements.push(element);
      childIds.forEach((childId) => buildNode(childId, container, worldPlacement));
    };

    this.propertySet.roots.forEach((rootId) => buildNode(rootId));

    this.resolvedDocument = {
      id: this.propertySet.id,
      name: this.propertySet.name,
      units: this.propertySet.units,
      metadata: this.propertySet.metadata ? cloneValue(this.propertySet.metadata) : undefined,
      assets,
      nodes,
      roots: [...this.propertySet.roots],
      relations: cloneValue(relations),
      elements,
    };

    this.userData.ifcElements = filterIfcElements(elements);
  }

  private clearResolvedScene() {
    if (this.resolvedDocument) {
      Object.values(this.resolvedDocument.nodes).forEach((node) => {
        node.element.dispose();
      });
    }

    this.clear();
    this.resolvedDocument = null;
  }

  private resolveAssets(): Record<string, OPBaseResolvedAsset> {
    return Object.fromEntries(
      Object.entries(this.propertySet.assets).map(([key, asset]) => {
        if (asset.kind === "embedded-op") {
          return [key, {
            id: asset.id,
            kind: asset.kind,
            definition: OpenElement.normalizeDefinition(asset.definition),
          } satisfies OPBaseResolvedAsset];
        }

        const candidates = resolveAssetSourceKeyCandidates(asset, this.assetBaseUrl);
        const source = candidates
          .map((candidate) => this.assetSources[candidate])
          .find((candidate) => candidate !== undefined);

        if (!source) {
          throw new Error(
            `External asset "${asset.uri}" is not loaded. Use loadOPBase() or provide assetSources when calling opBase().`,
          );
        }

        const definition = typeof source === "string"
          ? OpenElement.parseBuffer(source)
          : OpenElement.normalizeDefinition(source);

        return [key, {
          id: asset.id,
          kind: asset.kind,
          definition,
          sourceUri: resolveRuntimeUrl(asset.uri, this.assetBaseUrl),
        } satisfies OPBaseResolvedAsset];
      }),
    );
  }
}
